import { UsePaintModeProps, UsePaintModeReturn } from '../types';
import { yToPowerValue } from '../utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook for managing paint mode functionality in PowerProfileChart
 *
 * Features:
 * - Continuous line painting with mouse movement
 * - Left Mouse Button to activate painting
 * - Real-time preview with red line
 * - Interpolation between mouse positions for smooth lines
 * - Updates only painted points, leaves rest unchanged
 *
 * @param props - Configuration object for paint mode behavior
 * @returns Object with paint state and event handlers
 */
export const usePaintMode = ({
  enablePaint,
  chartData,
  chartRef,
  dynamicPowerLimits,
  onDataPaint,
  updateDynamicPowerLimits,
  onPaintEnd,
}: UsePaintModeProps): UsePaintModeReturn => {
  // Current paint operation state
  const [isPainting, setIsPainting] = useState(false);

  // Preview data showing the red line being painted
  const [previewData, setPreviewData] = useState<Map<number, number> | null>(null);

  // Track previous mouse position for interpolation
  const previousMousePositionRef = useRef<{ x: number; y: number; index: number } | null>(null);

  // Current power value during painting (for tooltip display)
  const [currentPaintValue, setCurrentPaintValue] = useState<number | null>(null);

  // Current mouse position for tooltip display
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  // Track recent paint values for line straightening
  const recentValuesRef = useRef<number[]>([]);

  /**
   * Straightens the line if recent values are similar (smooths out hand tremors)
   * @param newValue - New power value from mouse position
   * @returns Smoothed value if line should be straight, otherwise original value
   */
  const straightenLine = useCallback((newValue: number): number => {
    const WINDOW_SIZE = 8; // Number of recent points to consider
    const THRESHOLD = 5; // Max deviation (kW) to consider line as "straight"

    // Add new value to history
    recentValuesRef.current.push(newValue);
    if (recentValuesRef.current.length > WINDOW_SIZE) {
      recentValuesRef.current.shift();
    }

    // Need at least 3 points to detect straight line
    if (recentValuesRef.current.length < 3) {
      return newValue;
    }

    // Calculate average and max deviation
    const avg = recentValuesRef.current.reduce((sum, val) => sum + val, 0) / recentValuesRef.current.length;
    const maxDeviation = Math.max(...recentValuesRef.current.map((val) => Math.abs(val - avg)));

    // If all recent values are within threshold, snap to average (straight line)
    if (maxDeviation <= THRESHOLD) {
      return Math.round(avg * 10) / 10;
    }

    return newValue;
  }, []);

  /**
   * Calculates the data point index from mouse X position
   */
  const getPointIndexFromMouseX = useCallback(
    (mouseX: number, chartWidth: number): number => {
      if (chartData.length === 0) {
        return -1;
      }

      // Calculate relative position (0-1)
      const relativeX = Math.max(0, Math.min(1, mouseX / chartWidth));

      // Map to data point index
      const index = Math.floor(relativeX * chartData.length);

      return Math.max(0, Math.min(chartData.length - 1, index));
    },
    [chartData.length]
  );

  /**
   * Interpolates points between two positions for smooth painting
   */
  const interpolatePoints = useCallback(
    (startIndex: number, endIndex: number, startPower: number, endPower: number): Map<number, number> => {
      const points = new Map<number, number>();

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      for (let i = minIndex; i <= maxIndex; i++) {
        const t = maxIndex === minIndex ? 0 : (i - minIndex) / (maxIndex - minIndex);
        const power = startPower + t * (endPower - startPower);
        points.set(i, Math.max(0, Math.round(power * 10) / 10)); // Round to 1 decimal, min 0
      }

      return points;
    },
    []
  );

  /**
   * Updates preview data with new painted points
   */
  const updatePreviewData = useCallback(
    (mouseX: number, mouseY: number) => {
      if (!chartRef.current || !dynamicPowerLimits || chartData.length === 0) {
        return;
      }

      const rect = chartRef.current.getBoundingClientRect();

      // Recharts margins from config: { top: 10, right: 40, left: 20, bottom: 0 }
      // But Y-axis takes additional space (width: 80 from YAxis config)
      const marginTop = 10;
      const marginRight = 40;
      const marginLeft = 20;
      const yAxisWidth = 80; // From YAxis width prop in PowerProfileChart
      const xAxisHeight = 30; // Approximate height of XAxis

      // Total left offset is marginLeft + yAxisWidth
      const chartAreaLeft = marginLeft + yAxisWidth;
      const chartAreaTop = marginTop;
      const chartAreaWidth = rect.width - chartAreaLeft - marginRight;
      const chartAreaHeight = rect.height - marginTop - xAxisHeight; // Subtract XAxis height

      // Mouse position relative to chart area
      const relativeMouseX = mouseX - chartAreaLeft;
      const relativeMouseY = mouseY - chartAreaTop;

      // Allow painting slightly outside bounds (don't return early)
      // Just clamp the values instead
      const clampedMouseX = Math.max(0, Math.min(chartAreaWidth, relativeMouseX));

      // Calculate point index and power value
      const pointIndex = getPointIndexFromMouseX(clampedMouseX, chartAreaWidth);

      // Use relative Y (unclamped) to allow painting beyond current chart limits
      // Mouse position directly maps to power value - smooth and predictable
      const powerValue = yToPowerValue(
        relativeMouseY,
        chartAreaHeight,
        dynamicPowerLimits.minPower,
        dynamicPowerLimits.maxPower
      );

      // DON'T update power limits DURING painting - this causes the "acceleration" effect
      // Limits will be updated after painting is complete (in handleMouseUp)

      // Clamp power value to valid range (minimum 0 kW)
      const clampedPower = Math.max(0, Math.round(powerValue * 10) / 10);

      // Apply line straightening to smooth out hand tremors
      const straightenedPower = straightenLine(clampedPower);

      // Update current paint value and mouse position for tooltip
      setCurrentPaintValue(straightenedPower);
      setMousePosition({ x: mouseX, y: mouseY });

      setPreviewData((prev) => {
        const newPreview = new Map(prev || new Map());

        // Interpolate if we have a previous position
        if (previousMousePositionRef.current) {
          const prevIndex = previousMousePositionRef.current.index;
          const prevPower = previousMousePositionRef.current.y;

          const interpolatedPoints = interpolatePoints(prevIndex, pointIndex, prevPower, straightenedPower);
          interpolatedPoints.forEach((power, index) => {
            newPreview.set(index, power);
          });
        } else {
          // First point
          newPreview.set(pointIndex, straightenedPower);
        }

        // Update previous position
        previousMousePositionRef.current = {
          x: clampedMouseX,
          y: straightenedPower,
          index: pointIndex,
        };

        return newPreview;
      });
    },
    [
      chartRef,
      dynamicPowerLimits,
      chartData.length,
      getPointIndexFromMouseX,
      interpolatePoints,
      straightenLine,
      updateDynamicPowerLimits,
    ]
  );


  /**
   * Handles mouse down events to initiate paint operations
   */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!enablePaint || !chartRef.current || chartData.length === 0) {
        return;
      }

      // Check if left mouse button
      if (event.button !== 0) {
        return;
      }

      setIsPainting(true);
      setPreviewData(new Map());
      previousMousePositionRef.current = null;
      recentValuesRef.current = []; // Reset line straightening for new stroke

      // Start painting at current position
      const rect = chartRef.current.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      updatePreviewData(mouseX, mouseY);
    },
    [enablePaint, chartData.length, chartRef, updatePreviewData]
  );

  /**
   * Handles mouse up events to terminate paint operations
   * Includes interpolation to connect painted segment with existing data
   */
  const handleMouseUp = useCallback(() => {
    if (!isPainting) {
      return;
    }

    setIsPainting(false);

    // Apply preview data to chart data with edge interpolation
    // IMPORTANT: Do this BEFORE resetting limits so limits are calculated from new data
    if (previewData && previewData.size > 0 && onDataPaint) {
      // Get painted indices range
      const paintedIndices = Array.from(previewData.keys()).sort((a, b) => a - b);
      const firstPaintedIndex = paintedIndices[0];
      const lastPaintedIndex = paintedIndices[paintedIndices.length - 1];

      // Create final data with interpolation
      const finalPreviewData = new Map(previewData);

      // Interpolate before first painted point (connect to previous data)
      const interpolationSteps = 10; // Number of points for smooth transition
      if (firstPaintedIndex > 0) {
        const beforeIndex = firstPaintedIndex - 1;
        const beforePower = chartData[beforeIndex].power;
        const firstPaintedPower = previewData.get(firstPaintedIndex)!;

        // Add interpolation points
        const startInterpolation = Math.max(0, firstPaintedIndex - interpolationSteps);
        for (let i = startInterpolation; i < firstPaintedIndex; i++) {
          const t = (i - startInterpolation) / (firstPaintedIndex - startInterpolation);
          const interpolatedPower = beforePower + t * (firstPaintedPower - beforePower);
          finalPreviewData.set(i, Math.round(interpolatedPower * 10) / 10);
        }
      }

      // Interpolate after last painted point (connect to next data)
      if (lastPaintedIndex < chartData.length - 1) {
        const afterIndex = lastPaintedIndex + 1;
        const afterPower = chartData[afterIndex].power;
        const lastPaintedPower = previewData.get(lastPaintedIndex)!;

        // Add interpolation points
        const endInterpolation = Math.min(chartData.length - 1, lastPaintedIndex + interpolationSteps);
        for (let i = lastPaintedIndex + 1; i <= endInterpolation; i++) {
          const t = (i - lastPaintedIndex) / (endInterpolation - lastPaintedIndex);
          const interpolatedPower = lastPaintedPower + t * (afterPower - lastPaintedPower);
          finalPreviewData.set(i, Math.round(interpolatedPower * 10) / 10);
        }
      }

      // Apply all changes to chart data
      const newData = chartData.map((point, index) => {
        const paintedPower = finalPreviewData.get(index);
        if (paintedPower !== undefined) {
          return { ...point, power: paintedPower };
        }
        return point;
      });

      onDataPaint(newData);

      // Call onPaintEnd to trigger any cleanup/recalculation after painting
      if (onPaintEnd) {
        onPaintEnd();
      }
    }

    // Clear preview and previous position
    setPreviewData(null);
    setCurrentPaintValue(null);
    setMousePosition(null);
    previousMousePositionRef.current = null;
    recentValuesRef.current = []; // Reset line straightening history
  }, [isPainting, previewData, chartData, onDataPaint, onPaintEnd]);


  /**
   * Global mouse tracking during paint operations
   */
  useEffect(() => {
    if (isPainting && chartRef.current) {
      const handleGlobalMouseMove = (event: MouseEvent) => {
        if (!chartRef.current) {
          return;
        }

        const rect = chartRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        updatePreviewData(mouseX, mouseY);
      };

      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };

      // Throttle mouse move for performance (60fps = ~16ms)
      let lastUpdate = 0;
      const throttledMouseMove = (event: MouseEvent) => {
        const now = Date.now();
        if (now - lastUpdate >= 16) {
          handleGlobalMouseMove(event);
          lastUpdate = now;
        }
      };

      document.addEventListener('mousemove', throttledMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', throttledMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isPainting, chartRef, updatePreviewData, handleMouseUp]);

  /**
   * Converts preview data to PowerProfileDataPoint array for rendering
   * Only returns painted points, not the entire dataset
   */
  const getPreviewDataPoints = useCallback(() => {
    if (!previewData || previewData.size === 0) {
      return null;
    }

    // Get only painted indices
    const paintedIndices = Array.from(previewData.keys()).sort((a, b) => a - b);
    if (paintedIndices.length === 0) {
      return null;
    }

    // Return only painted points
    return paintedIndices.map((index) => ({
      ...chartData[index],
      power: previewData.get(index)!,
    }));
  }, [previewData, chartData]);

  return {
    isPainting,
    previewDataPoints: getPreviewDataPoints(),
    currentPaintValue,
    mousePosition,
    handleMouseDown,
    handleMouseUp,
  };
};
